/**
 * Creative preview lifecycle — explicit, finite, and independent of approval.
 *
 * The Stage Preview effect re-runs on every detail refresh (approval included)
 * because `versions` / `activeAsset` change object identity, not because the
 * asset changed. Tearing the preview down and re-attaching the SAME object URL
 * means the browser never re-fires `load`, so a media-loading flag set at that
 * moment would never clear — an endless spinner.
 *
 * These helpers keep that decision in one testable place.
 */

export const PREVIEW_STATE = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
  UNAVAILABLE: "unavailable",
});

/**
 * Is the already-attached preview still the correct one?
 *
 * True means: leave it alone. Do not reset readiness, do not release the object
 * URL, and do not re-enter `loading` — the operator's preview stays visible
 * through approval and any other detail refresh.
 */
export function shouldReuseExistingPreview({
  currentCacheKey,
  nextCacheKey,
  hasObjectUrl,
} = {}) {
  if (!hasObjectUrl) return false;
  if (!currentCacheKey || !nextCacheKey) return false;
  return String(currentCacheKey) === String(nextCacheKey);
}

/**
 * A browser-cached image can already be decoded before a newly attached
 * handler exists, so its `load` event never arrives. Resolve readiness from the
 * element itself rather than waiting for an event that will never fire.
 */
export function isImageAlreadyLoaded(node) {
  if (!node) return false;
  return Boolean(node.complete) && Number(node.naturalWidth) > 0;
}

/**
 * Terminal-state resolver. Every loading transition must reach one of these —
 * an endless spinner is never a valid resting state.
 */
export function resolvePreviewState({
  hasSource,
  isFetching,
  mediaLoading,
  error,
  unsupported,
} = {}) {
  if (error) return PREVIEW_STATE.ERROR;
  if (unsupported) return PREVIEW_STATE.UNAVAILABLE;
  if (!hasSource) return isFetching ? PREVIEW_STATE.LOADING : PREVIEW_STATE.IDLE;
  return mediaLoading ? PREVIEW_STATE.LOADING : PREVIEW_STATE.READY;
}

/**
 * Approval state and preview availability are independent concerns. Submitting
 * an approval must never move the preview into `loading`.
 */
export function previewStateAfterApprovalSubmit(previousState) {
  return previousState;
}
